import _ from 'lodash';
import { Editor, Node, Text, Transforms } from 'slate';
import { ELEMENT_PARAGRAPH } from '@udecode/plate';

import BLOCK_TYPES from 'components/text_editor_new/lib/block_types';
import getFirstNode from 'components/text_editor_new/lib/get_first_node';
import getLastNode from 'components/text_editor_new/lib/get_last_node';

export default function normalizeEditor(editor, options = {}) {
  // Trims whitespace from paragraphs or list nodes
  if (options.trimParagraphWhitespace) {
    // Go through each block, trim the text from the front
    for (const [, blockPath] of Editor.nodes(editor, {
      at: [],
      match: n => {
        return _.includes(BLOCK_TYPES, n.type);
      },
    })) {
      let firstTextEntry = getFirstNode(editor, {
        at: blockPath,
        match: n => Text.isText(n),
      });

      if (!firstTextEntry) {
        break;
      }

      const [firstText, firstTextPath] = firstTextEntry;
      const startTrimmed = firstText.text.replace(/^[^\S\r\n]+/, '');
      const firstStartDiff = firstText.text.length - startTrimmed.length;

      if (firstStartDiff > 0) {
        Transforms.delete(editor, {
          at: {
            offset: 0,
            path: firstTextPath,
          },
          distance: firstStartDiff,
        });
      }
    }

    // Go through each block, trim the text from the end (only space-like characters, not newlines)
    for (const [, blockPath] of Editor.nodes(editor, {
      at: [],
      match: n => {
        return _.includes(BLOCK_TYPES, n.type);
      },
    })) {
      let lastTextEntry = getLastNode(editor, {
        at: blockPath,
        match: n => Text.isText(n),
      });

      if (!lastTextEntry) {
        break;
      }

      const [lastText, lastTextPath] = lastTextEntry;
      const lastTrimmed = lastText.text.replace(/[^\S\r\n]+$/, '');
      const lastDiff = lastText.text.length - lastTrimmed.length;

      if (lastDiff > 0) {
        Transforms.delete(editor, {
          at: {
            offset: lastText.text.length,
            path: lastTextPath,
          },
          distance: lastDiff,
          reverse: true,
        });
      }
    }
  }

  if (options.trimEmptyParagraphs) {
    // Remove empty paragraphs at beginning of message
    if (editor.children.length === 1) {
      return;
    }

    let removeCount = 0;
    for (let i = 0; i < editor.children.length; i++) {
      const block = editor.children[i];

      if (block.type !== ELEMENT_PARAGRAPH || hasNonTextChildren(block)) {
        break;
      }

      const text = Node.string(block);
      if (text.trim() === '') {
        removeCount++;
      } else {
        break;
      }
    }

    if (removeCount === editor.children.length) {
      removeCount--;
    }

    _.times(removeCount, () => {
      // The first block of `editor.children` always has path [0].
      Transforms.removeNodes(editor, { at: [0] });
    });

    // Now remove empty paragraphs from the back.
    if (editor.children.length === 1) {
      return;
    }

    removeCount = 0;
    for (let i = editor.children.length - 1; i >= 0; i--) {
      const block = editor.children[i];

      if (block.type !== ELEMENT_PARAGRAPH || hasNonTextChildren(block)) {
        break;
      }

      const text = Node.string(block);
      if (text.trim() === '') {
        removeCount++;
      } else {
        break;
      }
    }

    if (removeCount === editor.children.length) {
      removeCount--;
    }

    _.times(removeCount, () => {
      // The last block of `editor.children` always has path [length-1].
      Transforms.removeNodes(editor, { at: [editor.children.length - 1] });
    });
  }
}

function hasNonTextChildren(node) {
  const nodeChildren = node?.children;

  if (_.isEmpty(nodeChildren)) {
    return !Text.isText(node);
  } else {
    return _.some(nodeChildren, child => hasNonTextChildren(child));
  }
}
